export const LD_FEATURES = [
  // --------- LONG-TERM FLAGS --------- //
  // These flags are meant to be long-lived to allow non-devs to control key areas of the app
  {
    name: 'promotedContent',
    ldKey: 'promoted-content',
    defaultValue: {},
  },
  {
    name: 'importantCommunicationBannerContent',
    ldKey: 'important-communication-banner-v2',
    defaultValue: {},
  },
  {
    name: 'sprigMXCohort',
    ldKey: 'sprig-mx-cohort',
    defaultValue: 'post-consultation',
  },
  {
    // owner: member stream
    name: 'mattermostPollingConfig',
    ldKey: 'mattermost-polling-config',
    defaultValue: {
      allChannels: {
        default: 120,
        fastCheck: 30,
      },
      currentChannel: {
        default: 2,
      },
    } satisfies ObjectFlags['mattermostPollingConfig'],
  },
  // --------- SHORT-TERM FLAGS --------- //
  // These flags are meant to wrap features that are in development and should be removed
  // within one quarter of release
  {
    // owner: aurora
    name: 'enableZendeskChat',
    ldKey: 'enable-zendesk-chat',
    defaultValue: false,
  },
  {
    // owner: aurora
    name: 'enableRegistrationHelpCentre',
    ldKey: 'enable-registration-help-centre',
    defaultValue: false,
  },
  {
    // owner: member stream
    name: 'enableTrustBasedEligibilitySearch',
    ldKey: 'enable-trust-based-eligibility',
    defaultValue: false,
  },
  {
    // owner: timelords
    // Time-based booking reads availabilities from Silkroad's v3 time_based route.
    name: 'enableAvailabilitiesV3TimeBasedSearch',
    ldKey: 'enable-availabilities-v3-time-based-search',
    defaultValue: false,
  },
  {
    // owner: client stream and foundation
    name: 'enableSeamlessSsoDeeplink',
    ldKey: 'enable-seamless-sso-deeplink',
    defaultValue: false,
  },
  {
    // owner: member stream
    name: 'enableNewEpisodeFetch',
    ldKey: 'enable-new-episode-fetch',
    defaultValue: true,
  },
  {
    /**
     * owner: provider stream (Cascades)
     * Enables fetching the Silkroad next tasks.
     */
    name: 'enableEpisodeNextTasks',
    ldKey: 'enable-episode-actions-ma',
    defaultValue: false,
  },
  {
    /**
     * owner: member stream
     * Partner brands that display each other's eligibility. dia always shows
     * every brand and is always shown, so listing it has no effect.
     */
    name: 'crossBrandEligibilityBrands',
    ldKey: 'cross-brand-eligibility-brands',
    defaultValue: [] satisfies ObjectFlags['crossBrandEligibilityBrands'],
  },
  {
    /**
     * owner: provider stream (Cascades)
     * Enables fetching the Silkroad provider-based availabilities v3.
     */
    name: 'enableAvailabilitiesV3ProviderBasedSearch',
    ldKey: 'enable-availabilities-v3-provider-based-search',
    defaultValue: false,
  },
  {
    /**
     * owner: member stream (House)
     * Enables the Care Jourmey screen while in development.
     */
    name: 'enableCareJourneyScreen',
    ldKey: 'enable-care-journey-screen',
    defaultValue: false,
  },
] as const

interface ObjectFlags {
  crossBrandEligibilityBrands: string[]
  importantCommunicationBannerContent: Record<string, unknown>
  mattermostPollingConfig: {
    allChannels: {
      default: number
      fastCheck: number
    }
    currentChannel: {
      default: number
    }
  }
  promotedContent: Record<string, unknown>
}

type StringFlagKeys = 'sprigMXCohort'

type FlagValueType<TKey extends (typeof LD_FEATURES)[number]['name']> =
  TKey extends keyof ObjectFlags
    ? ObjectFlags[TKey]
    : TKey extends StringFlagKeys
      ? string
      : boolean // default to boolean

export type LD_FEATURES_TYPE = {
  [K in (typeof LD_FEATURES)[number]['name']]: FlagValueType<K>
}
